import { connect } from 'react-redux';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useField } from 'react-final-form';

import { IKnmErknmFormEvent } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormEvent';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IKnmErknmFormRescheduling } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormRescheduling';
import { IDictionaryEventTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryEventTypeSelectOption';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { parseDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { getKnmStartDate } from 'KnmErknmPackage/MainModule/selectors/knmErknmSelectors';

import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { ApprovedTypesEnum } from '../../../APIPackage/KnmModule/enums/ApprovedTypesEnum';
import { ErknmPlanStatusesEnum } from '../../../APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import { ISelectOption } from '../../../UIPackage/MainModule/components/SelectInput';
import { IInteractionCategorySelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IInteractionCategorySelectOption';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { createKnmEventInspectorSelectOption } from '../../../CorePackage/CatalogsModule/common/createKnmEventInspectorSelectOption';
import { getUserRoleIsSupervisor } from '../../../AuthPackage/MainModule/selectors/authSelectors';
import { isLaterThanAdminSetting } from '../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import {
  getKnmDataCreated,
  getKnmDataErknmApproveAppeal,
  getKnmDataErknmApproveApproved,
  getKnmFormRef,
  getKnmIsErknmStatusDecisionAppealed,
  getKnmIsErknmStatusDeleted,
  getKnmIsErknmStatusTypeCanceled,
  getKnmIsErknmStatusTypeCantBeCarryOut,
  getKnmIsErknmStatusTypeCarriedOut,
  getKnmIsErknmStatusTypeCompleted,
  getKnmIsErknmStatusTypeWaitingCarryOut,
  getKnmIsNeedActSaveOrSign,
  getKnmWatchOnlyByTestUser,
} from '../../../KnmPackage/MainModule/selectors/knmSelectors';
import { getPlanListPlanErknmStatus } from '../../../PlanListPackage/MainModule/selectors/planListSelectors';
import { ErknmEventInspectors } from './ErknmEventInspectors';
import { IKnmErknmForm } from '../../MainModule/interfaces/IKnmErknmForm';
import { KnmEventsDocuments } from '../../../KnmPackage/KnmEventsDocuments/components/KnmEventsDocuments';
import { ErknmFieldMark } from '../../MainModule/components/ErknmFieldMark';
import { KnmLateFieldsRemark } from '../../../KnmPackage/KnmLateFieldsRemarkModule/components/KnmLateFieldsRemark';

interface IErknmEventItemBodyProps {
  eventFieldName: string;
  eventFieldValue: IKnmErknmFormEvent;
  eventId: string;
  interactionCategoryOptions: IInteractionCategorySelectOption[] | undefined;
  isActEvent?: boolean;
  isWatchOnly: boolean;
  knmEventTypesSelectOptions: IDictionaryEventTypeSelectOption[] | undefined;
  lateFieldName?: string;
  name: string;
  rescheduling?: IKnmErknmFormRescheduling[] | null | undefined;
  sIndex: number;
  setUniqKnmEventTypesSelectOptions: Dispatch<SetStateAction<IDictionaryEventTypeSelectOption[] | undefined>>;
  stopDate: string | undefined;
  uniqKnmEventTypesSelectOptions: IDictionaryEventTypeSelectOption[] | undefined;
}

interface IErknmEventItemBodyStoreProps {
  created: string | null | undefined;
  formRef: IKnmFormRef | undefined;
  isKnmStatusDecisionAppealed: boolean;
  isKnmStatusDeleted: boolean;
  isKnmStatusTypeCanceled: boolean;
  isKnmStatusTypeCantBeCarryOut: boolean;
  isKnmStatusTypeCarriedOut: boolean;
  isKnmStatusTypeCompleted: boolean;
  isKnmStatusTypeWaitingCarryOut: boolean;
  isSupervisor: boolean;
  knmDataApproveAppeal: boolean | null | undefined;
  knmDataApproved: ApprovedTypesEnum | null | undefined;
  knmPlanStatus: ErknmPlanStatusesEnum | undefined;
  settings: IAdminSetting[];
  isWatchOnlyByTestUser: boolean;
  isNeedActSaveOrSign?: boolean;
  startDate: string | undefined;
  isKindKnmForImpossibilityOfPrevious: boolean;
  isKindKnmForImpossibilityOfPreviousAllKindCode: boolean;
}

const ErknmEventItemBodyWithoutStore = ({
  eventId,
  eventFieldName,
  eventFieldValue,
  isActEvent,
  interactionCategoryOptions,
  isWatchOnly,
  knmEventTypesSelectOptions,
  lateFieldName,
  name,
  rescheduling,
  setUniqKnmEventTypesSelectOptions,
  sIndex,
  stopDate,
  uniqKnmEventTypesSelectOptions,
  created,
  formRef,
  isKnmStatusDecisionAppealed,
  isKnmStatusDeleted,
  isKnmStatusTypeCanceled,
  isKnmStatusTypeCantBeCarryOut,
  isKnmStatusTypeCarriedOut,
  isKnmStatusTypeCompleted,
  isKnmStatusTypeWaitingCarryOut,
  isSupervisor,
  knmDataApproveAppeal,
  knmDataApproved,
  knmPlanStatus,
  settings,
  isWatchOnlyByTestUser,
  isNeedActSaveOrSign = false,
  startDate,
  isKindKnmForImpossibilityOfPrevious,
  isKindKnmForImpossibilityOfPreviousAllKindCode,
}: IErknmEventItemBodyProps & IErknmEventItemBodyStoreProps) => {
  const isRequestingDocuments = eventFieldValue?.type?.label
    ? eventFieldValue.type.label.indexOf('Истребование документов') > -1
    : false;

  // Блокировка документов без учета основания по невозможности проведения предыдущей проверки,
  // по ней доступны статус представления, дата получения и файлы (TASK-9503)
  const isDocumentsSubmissionWatchOnly =
    isNeedActSaveOrSign ||
    isWatchOnlyByTestUser ||
    !isSupervisor ||
    isKnmStatusTypeCompleted ||
    isKnmStatusDeleted ||
    isKnmStatusTypeCanceled ||
    isKnmStatusTypeCantBeCarryOut ||
    isKnmStatusDecisionAppealed ||
    (isKnmStatusTypeCarriedOut && !isRequestingDocuments);

  const isDocumentsWatchOnly = isDocumentsSubmissionWatchOnly || isKindKnmForImpossibilityOfPreviousAllKindCode;

  const isNewReschedulingExist = Array.isArray(rescheduling) ? !!rescheduling?.find(i => i.isNew) : false;
  const lastRescheduling =
    Array.isArray(rescheduling) && rescheduling.length > 0 ? rescheduling[rescheduling.length - 1] : undefined;

  const startDateLastRescheduling = lastRescheduling?.newStartDate;
  const stopDateLastRescheduling = lastRescheduling?.newEndDate;

  const [inspectorOptions, setInspectorOptions] = useState<ISelectOption[]>([]);
  const [isWithoutActions, setWithoutAction] = useState<boolean>(
    eventFieldValue.type?.label === 'Осуществляется без действия',
  );

  useEffect(() => {
    setWithoutAction(eventFieldValue.type?.label === 'Осуществляется без действия');
  }, [eventFieldValue.type]);

  const isShow22 = isLaterThanAdminSetting(created, settings, AdminSettingCodesEnum.KNM_VALIDATION_4_1_22_STARTS_FROM);
  const isShow4155 = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_55_STARTS_FROM,
  );

  const isActiveTypeAndDates =
    isKnmStatusTypeWaitingCarryOut &&
    knmDataApproved === ApprovedTypesEnum.APPROVED &&
    !!knmDataApproveAppeal &&
    knmPlanStatus === ErknmPlanStatusesEnum.CONSIDERED;

  useEffect(() => {
    formRef?.current.getState().values.inspectorsErknm?.forEach(x => {
      if (x.fullName && x.guid && !inspectorOptions?.map(i => i.value).includes(x.fullName)) {
        const option = createKnmEventInspectorSelectOption(x.fullName, x.guid);

        inspectorOptions.push(option);
        setInspectorOptions(inspectorOptions);
      }
    });
  }, [formRef]);

  /**
   * Уникальные типы действия
   */
  const handleSetEventType = () => {
    // @ts-ignore
    const events: IKnmErknmFormEvent[] | undefined = formRef?.current.getFieldState(`${name}` as keyof IKnmErknmForm)
      ?.value;

    formRef?.current.change(`${eventFieldName}.interactionCategory` as keyof IKnmErknmForm);
    formRef?.current.change(`${eventFieldName}.inspectors` as keyof IKnmErknmForm);
    formRef?.current.change(`${eventFieldName}.startDate` as keyof IKnmErknmForm);
    formRef?.current.change(`${eventFieldName}.stopDate` as keyof IKnmErknmForm);
    formRef?.current.change(`${eventFieldName}.submittedDocuments` as keyof IKnmErknmForm);

    if (!events || isActEvent) {
      setUniqKnmEventTypesSelectOptions(knmEventTypesSelectOptions);
    } else {
      const eventsWithTypes = events.filter(e => e.type !== undefined);

      if (!eventsWithTypes) {
        setUniqKnmEventTypesSelectOptions(knmEventTypesSelectOptions);
      } else {
        const selectedTypes: IDictionaryEventTypeSelectOption[] = [];

        eventsWithTypes.forEach(x => (x.type ? selectedTypes.push(x.type) : undefined));

        let uniq = knmEventTypesSelectOptions;

        // eslint-disable-next-line no-restricted-syntax
        for (const a of selectedTypes) {
          uniq = uniq?.filter(x => x.label !== a.label);
        }

        setUniqKnmEventTypesSelectOptions(uniq);
      }
    }
  };

  const parseStartDate = parseDate({ date: startDate || '', parseFormat: DATE_FORMAT_LONG_FNS });
  const parseStopDate = parseDate({ date: stopDate || '', parseFormat: DATE_FORMAT_LONG_FNS });
  const startDateEventField = useField(`${eventFieldName}.startDate`, { subscription: { value: true } });
  const startDateEventValue = startDateEventField.input.value;
  const parseStartDateEventValue = parseDate({ date: startDateEventValue || '', parseFormat: DATE_FORMAT_LONG_FNS });
  const stopDateEventField = useField(`${eventFieldName}.stopDate`, { subscription: { value: true } });
  const stopDateEventValue = stopDateEventField.input.value;

  const newParseStartDate = parseDate({ date: startDateLastRescheduling || '', parseFormat: DATE_FORMAT_LONG_FNS });
  const newParseStopDate = parseDate({ date: stopDateLastRescheduling || '', parseFormat: DATE_FORMAT_LONG_FNS });

  return (
    <>
      <Row notWrap>
        <FieldBlock id={`${eventId}.type`}>
          <SelectInputField
            name={`${eventFieldName}.type`}
            placeholder={
              <>
                Выберите действие
                <ErknmFieldMark fieldName={`${eventFieldName}.type`} />
              </>
            }
            options={uniqKnmEventTypesSelectOptions}
            disabled={(isWatchOnly && !isActiveTypeAndDates) || eventFieldValue.isFilledFromLink === true}
            size="large"
            onChange={handleSetEventType}
          />
        </FieldBlock>
        {isActEvent && (
          <KnmLateFieldsRemark
            lateFieldPath={`${lateFieldName}/event/title`}
            fieldId={`${eventId}.type`}
            isLeftPosition={true}
          />
        )}
      </Row>
      {isShow4155 && !isActEvent && (
        <Row notWrap>
          <FieldBlock id={`${eventId}.interactionCategory`}>
            <SelectInputField
              name={`${eventFieldName}.interactionCategory`}
              placeholder={
                <>
                  Выберите категорию взаимодействия
                  <ErknmFieldMark fieldName={`${eventFieldName}.interactionCategory`} />
                </>
              }
              options={interactionCategoryOptions}
              disabled={isWatchOnly && !isKindKnmForImpossibilityOfPrevious}
              size="large"
            />
          </FieldBlock>
          {isActEvent && (
            <KnmLateFieldsRemark
              lateFieldPath={`${lateFieldName}/event/interactionCategory`}
              fieldId={`${eventId}.interactionCategory`}
              isLeftPosition={true}
            />
          )}
        </Row>
      )}
      {!isWithoutActions && (
        <>
          <Row notWrap>
            <FieldBlock id={`${eventId}.startDate`} size="half" margin="right">
              <DatePickerField
                name={`${eventFieldName}.startDate`}
                placeholder={
                  <>
                    Дата начала
                    <ErknmFieldMark fieldName={`${eventFieldName}.startDate`} />
                  </>
                }
                disabled={
                  isNewReschedulingExist
                    ? false
                    : isWatchOnly && !isActiveTypeAndDates && !isKindKnmForImpossibilityOfPreviousAllKindCode
                }
                minDate={newParseStartDate || parseStartDate}
                maxDate={newParseStopDate || parseStopDate}
              />
            </FieldBlock>
            {isActEvent && (
              <KnmLateFieldsRemark
                lateFieldPath={`${lateFieldName}/event/startDate`}
                fieldId={`${eventId}.startDate`}
              />
            )}
            <FieldBlock id={`${eventId}.stopDate`} size="half">
              <DatePickerField
                name={`${eventFieldName}.stopDate`}
                key={stopDate}
                placeholder={
                  <>
                    Дата окончания
                    <ErknmFieldMark fieldName={`${eventFieldName}.stopDate`} />
                  </>
                }
                disabled={
                  isNewReschedulingExist
                    ? false
                    : isWatchOnly && !isActiveTypeAndDates && !isKindKnmForImpossibilityOfPreviousAllKindCode
                }
                minDate={parseStartDateEventValue ?? (newParseStartDate || parseStartDate)}
                maxDate={newParseStopDate || parseStopDate}
              />
            </FieldBlock>
            {isActEvent && (
              <KnmLateFieldsRemark
                lateFieldPath={`${lateFieldName}/event/stopDate`}
                fieldId={`${eventId}.stopDate`}
                isLeftPosition={true}
              />
            )}
          </Row>
          {isShow4155 && isActEvent && (
            <ErknmEventInspectors
              name={`${eventFieldName}.inspectors`}
              eventId={eventId}
              watchOnly={isWatchOnly}
              lateFieldName={`${lateFieldName}/event`}
            />
          )}
        </>
      )}
      {isShow22 && !isActEvent && (eventFieldValue?.type?.label || '').indexOf('Истребование документов') > -1 && (
        <KnmEventsDocuments
          sIndex={sIndex}
          formRef={formRef}
          eventFieldName={eventFieldName}
          isWatchOnly={isDocumentsWatchOnly}
          isSubmissionWatchOnly={isDocumentsSubmissionWatchOnly}
          isKnm
          startDateEvent={startDateEventValue}
          stopDateEvent={stopDateEventValue}
          isFilledFromLink={eventFieldValue.isFilledFromLink === true}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: IRootState) => ({
  created: getKnmDataCreated(state),
  formRef: getKnmFormRef(state),
  isKnmStatusDecisionAppealed: getKnmIsErknmStatusDecisionAppealed(state),
  isKnmStatusDeleted: getKnmIsErknmStatusDeleted(state),
  isKnmStatusTypeCanceled: getKnmIsErknmStatusTypeCanceled(state),
  isKnmStatusTypeCantBeCarryOut: getKnmIsErknmStatusTypeCantBeCarryOut(state),
  isKnmStatusTypeCarriedOut: getKnmIsErknmStatusTypeCarriedOut(state),
  isKnmStatusTypeCompleted: getKnmIsErknmStatusTypeCompleted(state),
  isKnmStatusTypeWaitingCarryOut: getKnmIsErknmStatusTypeWaitingCarryOut(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  knmDataApproveAppeal: getKnmDataErknmApproveAppeal(state),
  knmDataApproved: getKnmDataErknmApproveApproved(state),
  knmPlanStatus: getPlanListPlanErknmStatus(state),
  settings: getAdminFetchSettings(state),
  isWatchOnlyByTestUser: getKnmWatchOnlyByTestUser(state),
  isNeedActSaveOrSign: getKnmIsNeedActSaveOrSign(state),
  startDate: getKnmStartDate(state),
});

export const ErknmEventItemBody = connect(mapStateToProps)(ErknmEventItemBodyWithoutStore);
